import { useEffect, useState } from 'react'

import { Button } from '@mantine/core'

import { navigate, routes, useLocation, useParams } from '@redwoodjs/router'
import { MetaTags, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import ApplicationLoadingScreen from 'src/components/Feedback/ApplicationLoadingScreen/ApplicationLoadingScreen'
import {
  applicationPathForStep,
  forwardQuery,
  isDriverStep,
  LOGIN_ONLY_PARAMS,
} from 'src/lib/driverDeepLink'

const PUBLIC_COMPANY_BY_SLUG = gql`
  query PublicCompanyBySlug($slug: String!) {
    publicCompanyBySlug(slug: $slug) {
      companyId
      companyName
    }
  }
`

const MY_APPLICATION_FOR_COMPANY = gql`
  query MyApplicationForCompany($companyId: String!) {
    myApplicationForCompany(companyId: $companyId)
  }
`

const CompanySlugPage = () => {
  // Public entry is /c/{companySlug} (path), with an optional step segment
  // such as /c/{companySlug}/review. Legacy links used a /c?slug=… callback
  // where the slug arrives as a query param, and ?s=review for the step —
  // still read here so old links keep working, but we no longer generate
  // those forms.
  const { companySlug, slug, phone, s, step: stepSegment } = useParams()
  const { search } = useLocation()
  const effectiveSlug = companySlug || slug
  const step = stepSegment || s
  const unknownStep = Boolean(stepSegment) && !isDriverStep(stepSegment)
  const { currentUser, loading: authLoading } = useAuth()

  // /c is a driver entry point — an admin/recruiter session still goes through
  // the driver SMS login, not treated as already logged in.
  const isDriver = !!currentUser?.roles?.includes('DRIVER')

  // A query that *fails* is transient (network/server), not a missing company
  // or a permission problem — show a retry instead of /not-found or /forbidden.
  const [loadError, setLoadError] = useState(false)

  const { data: companyData, loading: companyLoading } = useQuery(
    PUBLIC_COMPANY_BY_SLUG,
    {
      variables: { slug: effectiveSlug },
      skip: !effectiveSlug || unknownStep,
      onError: () => setLoadError(true),
    }
  )
  const company = companyData?.publicCompanyBySlug
  const companyId = company?.companyId

  // Invalid slug or an unknown step segment → not found.
  useEffect(() => {
    if (unknownStep || (!companyLoading && companyData && !company)) {
      navigate('/not-found')
    }
  }, [unknownStep, companyLoading, companyData, company])

  // Company resolved but not authenticated → SMS login, carrying the slug and
  // step so the login leg can resolve the applicant inline and route onward.
  useEffect(() => {
    if (authLoading || companyLoading || !company || isDriver || unknownStep)
      return
    navigate(
      `/driver-login${forwardQuery(search, { slug: effectiveSlug, s: step })}`
    )
  }, [
    authLoading,
    companyLoading,
    company,
    isDriver,
    unknownStep,
    effectiveSlug,
    step,
    search,
  ])

  const { data: appData, loading: appLoading } = useQuery(
    MY_APPLICATION_FOR_COMPANY,
    {
      variables: { companyId },
      skip: !companyId || !isDriver || unknownStep,
      // UNAUTHENTICATED = a new driver (no user record yet) → "no application".
      // FORBIDDEN = a permission denial (not retryable). Else = transient.
      onError: (error) => {
        const codes = error.graphQLErrors?.map((e) => e.extensions?.code) ?? []
        if (codes.includes('UNAUTHENTICATED') && companyId) {
          navigate(
            `${routes.publicShortForm({ companyId })}${forwardQuery(
              search,
              { phone },
              LOGIN_ONLY_PARAMS
            )}`
          )
        } else if (codes.includes('FORBIDDEN')) {
          navigate(routes.forbidden())
        } else {
          setLoadError(true)
        }
      },
    }
  )

  useEffect(() => {
    if (!isDriver || !companyId || appLoading || !appData) return
    const applicationId = appData.myApplicationForCompany
    if (applicationId) {
      navigate(
        `${applicationPathForStep(step)}${forwardQuery(
          search,
          { applicationId },
          LOGIN_ONLY_PARAMS
        )}`
      )
    } else {
      // Pass the verified phone so the short form can skip a second login.
      navigate(
        `${routes.publicShortForm({ companyId })}${forwardQuery(
          search,
          { phone: currentUser?.phone },
          LOGIN_ONLY_PARAMS
        )}`
      )
    }
  }, [isDriver, currentUser, companyId, appLoading, appData, step, search])

  if (loadError) {
    return (
      <>
        <MetaTags title="Application" />
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Something went wrong
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            We couldn&apos;t load this page. Please try again.
          </div>
          <Button size="md" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <MetaTags title={company?.companyName || 'Application'} />
      <ApplicationLoadingScreen />
    </>
  )
}

export default CompanySlugPage
